import { faArrowUpRightFromSquare, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useState } from "react";

import { IdentityProviderType } from "@skydio/pbtypes/pbtypes/tools/cloud_api/enums_pb";
import { Button, Divider, Modal, Select } from "@skydio/rivet-ui";

import NewAxonConnectProviderPane from "./NewAxonConnectProviderPane";
import NewOidcProviderPane from "./NewOidcProviderPane";
import NewSamlProviderPane from "./NewSamlProviderPane";

import type { ValueType } from "@skydio/core";
import type { Key } from "@skydio/rivet-ui";

const SsoSupportLink = "https://support.skydio.com/hc/articles/33456277678235";

interface NewSsoModalContentProps {
  orgNodeId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCompleted?: () => void;
  connection?: string;
  axonExists?: boolean;
}

const NewSsoModalContent = ({
  orgNodeId,
  isOpen,
  onOpenChange,
  onCompleted,
  connection,
  axonExists,
}: NewSsoModalContentProps) => {
  const [selectedType, setSelectedType] =
    useState<ValueType<IdentityProviderType.IdentityProviderTypeEnumMap> | null>(null);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      onOpenChange(isOpen);
      if (!isOpen) {
        // Clear everything when the modal is closed
        setSelectedType(null);
      }
    },
    [onOpenChange, setSelectedType]
  );

  const handleClose = useCallback(() => {
    onCompleted?.();
  }, [onCompleted]);

  const handlePaneChange = useCallback(() => {
    // Clear the selected type when the pane changes
    setSelectedType(null);
    onOpenChange(false);
  }, [onOpenChange, setSelectedType]);

  const handleSelectedTypeChange = useCallback(
    (key: Key | null) => {
      setSelectedType(key as ValueType<IdentityProviderType.IdentityProviderTypeEnumMap>);
    },
    [setSelectedType]
  );

  return (
    <Modal
      title="New SSO Provider"
      size="3xl"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      onClose={handleClose}
    >
      <Modal.Content>
        {/* Set Type page in modal and include forms */}
        <Modal.Header>
          <Modal.Title>Select Provider Type</Modal.Title>
        </Modal.Header>
        <Modal.Body className="mb-4">
          <Select
            className="w-full"
            aria-label="SSO Type"
            placeholder="SSO Type"
            items={Object.entries(IdentityProviderType.IdentityProviderTypeEnum)
              .filter(k => {
                return k[0] != "UNKNOWN";
              })
              .filter(k => {
                // Filter out Axon if it already exists
                return !(k[0] == "AXON" && axonExists);
              })
              .map(([key, enumValue]) => {
                switch (key) {
                  case "AXON": // Pretty print Axon
                    return { key: enumValue, label: "Axon Connect" };
                  default:
                    return { key: enumValue, label: key };
                }
              })}
            onSingleSelectionChange={handleSelectedTypeChange}
          >
            {item => <Select.Item key={item.key}>{item.label}</Select.Item>}
          </Select>
          {selectedType !== null && <Divider />}
          {selectedType == IdentityProviderType.IdentityProviderTypeEnum.SAML && (
            <NewSamlProviderPane
              organizationNodeId={orgNodeId}
              connection={connection}
              onNext={handlePaneChange}
              onCancel={handlePaneChange}
            />
          )}
          {selectedType == IdentityProviderType.IdentityProviderTypeEnum.OIDC && (
            <NewOidcProviderPane
              organizationNodeId={orgNodeId}
              connection={connection}
              onNext={handlePaneChange}
              onCancel={handlePaneChange}
            />
          )}
          {selectedType == IdentityProviderType.IdentityProviderTypeEnum.AXON && (
            <NewAxonConnectProviderPane
              organizationNodeId={orgNodeId}
              connection={connection}
              onNext={handlePaneChange}
              onCancel={handlePaneChange}
            />
          )}
        </Modal.Body>
        {selectedType === null && (
          <Modal.Footer className="flex justify-start pt-2">
            <a href={SsoSupportLink} target="_blank" rel="noreferrer">
              Learn more about configuring SSO <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </a>
          </Modal.Footer>
        )}
      </Modal.Content>
    </Modal>
  );
};

interface NewSsoModalProps {
  orgNodeId: string;
  onNew?: () => void;
  connection?: string;
  axonExists?: boolean;
}
const NewSsoModal = ({ orgNodeId, onNew, connection, axonExists }: NewSsoModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  return (
    <>
      <Button size="lg" fullWidth={true} onClick={handleOpen}>
        <FontAwesomeIcon icon={faPlus} /> Add Login Method
      </Button>
      <NewSsoModalContent
        orgNodeId={orgNodeId}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        onCompleted={onNew}
        connection={connection}
        axonExists={axonExists}
      />
    </>
  );
};

export default NewSsoModal;
